export const INTRUDER_FAMILY_LABELS = ["Intruder", "Intruder +"] as const;
export const MOTION_PLUS_LABELS = ["Motion Plus", "Motion +"] as const;
export const LINE_CROSSING_LABEL = "Line Crossing";

export type ProductOptionRow = readonly [string, ...unknown[]];

type TrioProductGroup = "intruder" | "motion-plus" | "line-crossing";

const getTrioGroupForLabel = (label: string): TrioProductGroup | null => {
  if (
    INTRUDER_FAMILY_LABELS.includes(
      label as typeof INTRUDER_FAMILY_LABELS[number]
    )
  ) {
    return "intruder";
  }
  if (MOTION_PLUS_LABELS.includes(label as typeof MOTION_PLUS_LABELS[number])) {
    return "motion-plus";
  }
  if (label === LINE_CROSSING_LABEL) {
    return "line-crossing";
  }
  return null;
};

export const getConflictingProductDisplayNames = (
  selectedDisplayNames: readonly string[]
): Set<string> => {
  const selectedSet = new Set(selectedDisplayNames);
  const hideDisplayNames = new Set<string>();

  const selectedGroups = new Set<TrioProductGroup>();
  selectedDisplayNames.forEach((label) => {
    const group = getTrioGroupForLabel(label);
    if (group) {
      selectedGroups.add(group);
    }
  });

  if (selectedSet.has("Intruder")) {
    hideDisplayNames.add("Intruder +");
  }
  if (selectedSet.has("Intruder +")) {
    hideDisplayNames.add("Intruder");
  }

  if (selectedGroups.has("intruder")) {
    MOTION_PLUS_LABELS.forEach((label) => hideDisplayNames.add(label));
    hideDisplayNames.add(LINE_CROSSING_LABEL);
  }
  if (selectedGroups.has("motion-plus")) {
    INTRUDER_FAMILY_LABELS.forEach((label) => hideDisplayNames.add(label));
    hideDisplayNames.add(LINE_CROSSING_LABEL);
  }
  if (selectedGroups.has("line-crossing")) {
    INTRUDER_FAMILY_LABELS.forEach((label) => hideDisplayNames.add(label));
    MOTION_PLUS_LABELS.forEach((label) => hideDisplayNames.add(label));
  }

  return hideDisplayNames;
};

export const filterCameraProductOptionsByTrioExclusion = <
  T extends ProductOptionRow
>(
  baseOptions: readonly T[],
  selectedDisplayNames: readonly string[]
): T[] => {
  const hiddenDisplayNames =
    getConflictingProductDisplayNames(selectedDisplayNames);
  return baseOptions.filter((row) => !hiddenDisplayNames.has(row[0]));
};

export const getBlockedProductIdsForSelectedIds = (
  selectedProductIds: readonly number[],
  productsArray: readonly [string, number][]
): Set<number> => {
  const idToName = new Map<number, string>();
  const nameToId = new Map<string, number>();
  productsArray.forEach(([name, id]) => {
    idToName.set(id, name);
    nameToId.set(name, id);
  });

  const selectedDisplayNames = selectedProductIds
    .map((id) => idToName.get(id))
    .filter((name): name is string => name !== undefined);

  const hiddenDisplayNames =
    getConflictingProductDisplayNames(selectedDisplayNames);
  const hiddenProductIds = new Set<number>();
  hiddenDisplayNames.forEach((name) => {
    const id = nameToId.get(name);
    if (id !== undefined && !selectedProductIds.includes(id)) {
      hiddenProductIds.add(id);
    }
  });

  return hiddenProductIds;
};

export const isProductNameInTrio = (productName: string): boolean =>
  getTrioGroupForLabel(productName) !== null;
